import {
  Activity,
  BarChart3,
  Briefcase,
  Building2,
  CalendarDays,
  ClipboardCheck,
  FileText,
  HardHat,
  KanbanSquare,
  LayoutDashboard,
  MapPin,
  Megaphone,
  Plug,
  Radio,
  Receipt,
  Rocket,
  Settings,
  ShieldCheck,
  Tag,
  Users,
  Wallet,
} from "lucide-react";
import { Link, NavLink } from "react-router-dom";

import { AppIcon } from "@/components/app-icon";
import { cn } from "@/lib/utils";
import { useAuth } from "@/stores/auth";

type NavItem = {
  to: string;
  label: string;
  icon: typeof LayoutDashboard;
  adminOnly?: boolean;
  section?: string;
};

const NAV: NavItem[] = [
  // Operação
  { to: "/", label: "Dashboard", icon: LayoutDashboard, section: "Operação" },
  { to: "/jobs", label: "Chamados", icon: Briefcase, section: "Operação" },
  { to: "/calendar", label: "Calendário", icon: CalendarDays, section: "Operação" },
  { to: "/dispatch", label: "Dispatch", icon: Radio, section: "Operação" },
  // Cadastros
  { to: "/clients", label: "Clientes", icon: Building2, section: "Cadastros" },
  { to: "/nerds", label: "Técnicos", icon: HardHat, section: "Cadastros" },
  { to: "/contracts", label: "Contratos", icon: FileText, section: "Cadastros" },
  { to: "/categories", label: "Categorias", icon: Tag, section: "Cadastros" },
  { to: "/cidades", label: "Cidades", icon: MapPin, section: "Cadastros" },
  // Financeiro
  { to: "/invoices", label: "Faturas", icon: Receipt, section: "Financeiro" },
  { to: "/closings", label: "Fechamentos", icon: ClipboardCheck, section: "Financeiro" },
  { to: "/payment-windows", label: "Janelas pagto.", icon: CalendarDays, section: "Financeiro" },
  // Análise
  { to: "/reports", label: "Relatórios", icon: BarChart3, section: "Análise" },
  { to: "/changelog", label: "Changelog", icon: Rocket, section: "Análise" },
  { to: "/delivery", label: "Delivery", icon: KanbanSquare, section: "Análise" },
  { to: "/activity", label: "Atividades", icon: Activity, section: "Análise", adminOnly: true },
  // Admin
  { to: "/users", label: "Usuários", icon: Users, section: "Admin", adminOnly: true },
  { to: "/integrations", label: "Integrações", icon: Plug, section: "Admin", adminOnly: true },
  { to: "/announce", label: "Comunicado", icon: Megaphone, section: "Admin", adminOnly: true },
  { to: "/settings", label: "Configurações", icon: Settings, section: "Admin" },
];

// Nav do PORTAL (cliente/técnico) — só os próprios dados.
const PORTAL_NAV: NavItem[] = [
  { to: "/portal", label: "Meu painel", icon: Wallet, section: "Minha conta" },
  { to: "/changelog", label: "Novidades", icon: Rocket, section: "Minha conta" },
  { to: "/delivery", label: "Reportar", icon: KanbanSquare, section: "Minha conta" },
  { to: "/profile", label: "Perfil", icon: Users, section: "Minha conta" },
  { to: "/settings", label: "Configurações", icon: Settings, section: "Minha conta" },
];

interface SidebarProps {
  /** Pra mobile drawer */
  onItemClick?: () => void;
  /** Mostra cabeçalho (mobile drawer) */
  withHeader?: boolean;
  /** Force visible mesmo no mobile (sheet) */
  alwaysVisible?: boolean;
}

export function Sidebar({ onItemClick, withHeader, alwaysVisible }: SidebarProps = {}) {
  const { user, impersonation } = useAuth();
  const isAdmin = user?.role === "super_admin" || user?.role === "admin" || !!impersonation;
  const isPortalUser =
    !impersonation && (user?.role === "client" || user?.role === "nerd");
  const items = isPortalUser ? PORTAL_NAV : NAV.filter((n) => !n.adminOnly || isAdmin);

  // Agrupa por section preservando ordem
  const sections: { name: string; items: NavItem[] }[] = [];
  for (const item of items) {
    const sec = item.section ?? "Outros";
    let bucket = sections.find((s) => s.name === sec);
    if (!bucket) {
      bucket = { name: sec, items: [] };
      sections.push(bucket);
    }
    bucket.items.push(item);
  }

  return (
    <aside
      className={cn(
        "w-60 shrink-0 flex flex-col",
        !alwaysVisible && "hidden md:flex sticky top-16 h-[calc(100dvh-4rem)]",
        alwaysVisible && "h-full",
        "border-r border-slate-200/60 dark:border-white/5",
        "bg-white/40 dark:bg-card/30 backdrop-blur-xl",
      )}
    >
      {withHeader && (
        <Link
          to="/"
          onClick={onItemClick}
          className="flex items-center gap-2.5 px-5 h-16 border-b border-slate-200/60 dark:border-white/5"
        >
          <AppIcon variant="backoffice" size="md" />
          <span className="font-bold tracking-tight">op-next</span>
        </Link>
      )}
      {impersonation && (
        <div className="px-3 pt-3">
          <div className="rounded-lg bg-warning-50 dark:bg-warning-100/10 px-2.5 py-2 text-[11px] text-warning-700 flex items-center gap-1.5">
            <ShieldCheck className="h-3 w-3" aria-hidden /> Impersonating
          </div>
        </div>
      )}
      <nav
        className={cn(
          "flex-1 overflow-y-auto px-3 py-4 space-y-4",
          // Scrollbar discreta + smooth scroll
          "[scrollbar-width:thin] [scrollbar-color:rgb(203_213_225/0.4)_transparent]",
          "[&::-webkit-scrollbar]:w-1.5",
          "[&::-webkit-scrollbar-track]:bg-transparent",
          "[&::-webkit-scrollbar-thumb]:bg-slate-300/40 [&::-webkit-scrollbar-thumb]:rounded-full",
          "[&::-webkit-scrollbar-thumb:hover]:bg-slate-400/60",
          "dark:[&::-webkit-scrollbar-thumb]:bg-white/10",
          "dark:[&::-webkit-scrollbar-thumb:hover]:bg-white/20",
          "scroll-smooth",
        )}
      >
        {sections.map((sec) => (
          <div key={sec.name}>
            <p className="px-3 mb-1 text-[10px] font-semibold uppercase tracking-wider text-muted-foreground">
              {sec.name}
            </p>
            <div className="space-y-0.5">
              {sec.items.map(({ to, label, icon: Icon }) => (
                <NavLink
                  key={to}
                  to={to}
                  end={to === "/"}
                  onClick={onItemClick}
                  className={({ isActive }) =>
                    cn(
                      "group relative flex items-center gap-3",
                      "rounded-xl px-3 py-2 text-sm font-medium",
                      "transition-all duration-200",
                      isActive
                        ? "bg-primary-600 text-primary-foreground shadow-[0_1px_2px_rgba(15,23,42,0.06),0_4px_12px_-4px_rgba(28,151,200,0.45)]"
                        : "text-slate-700 dark:text-slate-300 hover:bg-slate-100/80 dark:hover:bg-white/5 hover:text-foreground",
                    )
                  }
                >
                  {({ isActive }) => (
                    <>
                      <Icon
                        className={cn(
                          "h-[16px] w-[16px] shrink-0 transition-transform",
                          !isActive && "group-hover:scale-110",
                        )}
                        aria-hidden
                      />
                      <span className="truncate">{label}</span>
                    </>
                  )}
                </NavLink>
              ))}
            </div>
          </div>
        ))}
      </nav>
      <div className="border-t border-slate-200/60 dark:border-white/5 p-4 text-xs text-muted-foreground">
        <p className="font-medium text-foreground">op-next</p>
        <p>v0.1.0 · {import.meta.env.MODE}</p>
      </div>
    </aside>
  );
}
